import React, { useState, useEffect } from 'react';
import { Clock, Calculator, Search, Globe, Cog, ChevronRight, Loader2, Check, Terminal, FileCode, GitBranch, Plus, Link2, Settings, Zap, List, Database, FileText, Trash2, PlusCircle, Pencil } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import ToolCallResultRenderer from './ToolCallResultRenderer';

const TOOL_META = {
  get_current_time: {
    label: 'Current Time',
    description: 'Checking the current date and time',
    doneDescription: 'Retrieved current date and time',
    icon: Clock,
  },
  calculator: {
    label: 'Calculator',
    description: 'Calculating a math expression',
    doneDescription: 'Calculated result',
    icon: Calculator,
  },
  web_search: {
    label: 'Web Search',
    description: 'Searching the web',
    doneDescription: 'Found web results',
    icon: Search,
  },
  web_fetch: {
    label: 'Web Fetch',
    description: 'Fetching webpage content',
    doneDescription: 'Fetched webpage content',
    icon: Globe,
  },
  bash_code_execution: {
    label: 'Code Execution',
    description: 'Running code',
    doneDescription: 'Code executed',
    icon: Terminal,
  },
  text_editor_code_execution: {
    label: 'File Editor',
    description: 'Editing a file',
    doneDescription: 'File updated',
    icon: FileCode,
  },
  // Workflow tools
  create_workflow: {
    label: 'Create Workflow',
    description: 'Creating workflow',
    doneDescription: 'Workflow created',
    icon: GitBranch,
    isWorkflow: true,
  },
  add_workflow_node: {
    label: 'Add Node',
    description: 'Adding node to workflow',
    doneDescription: 'Node added',
    icon: Plus,
    isWorkflow: true,
  },
  connect_workflow_nodes: {
    label: 'Connect Nodes',
    description: 'Connecting workflow nodes',
    doneDescription: 'Nodes connected',
    icon: Link2,
    isWorkflow: true,
  },
  configure_workflow_node: {
    label: 'Configure Node',
    description: 'Configuring node',
    doneDescription: 'Node configured',
    icon: Settings,
    isWorkflow: true,
  },
  activate_workflow: {
    label: 'Activate Workflow',
    description: 'Activating workflow',
    doneDescription: 'Workflow activated',
    icon: Zap,
    isWorkflow: true,
  },
  get_workflow_summary: {
    label: 'Workflow Summary',
    description: 'Getting workflow summary',
    doneDescription: 'Summary retrieved',
    icon: GitBranch,
    isWorkflow: true,
  },
  remove_workflow_node: {
    label: 'Remove Node',
    description: 'Removing node from workflow',
    doneDescription: 'Node removed',
    icon: Trash2,
    isWorkflow: true,
  },
  delete_workflow: {
    label: 'Delete Workflow',
    description: 'Deleting workflow',
    doneDescription: 'Workflow deleted',
    icon: Trash2,
    isWorkflow: true,
  },
  // Context tools
  list_user_forms: {
    label: 'List Forms',
    description: 'Looking up your forms',
    doneDescription: 'Found your forms',
    icon: FileText,
    isWorkflow: true,
  },
  list_user_object_definitions: {
    label: 'List CRM Objects',
    description: 'Looking up CRM object types',
    doneDescription: 'Found CRM objects',
    icon: Database,
    isWorkflow: true,
  },
  list_user_workflows: {
    label: 'List Workflows',
    description: 'Looking up your workflows',
    doneDescription: 'Found your workflows',
    icon: GitBranch,
    isWorkflow: true,
  },
  list_available_node_types: {
    label: 'Available Nodes',
    description: 'Checking available node types',
    doneDescription: 'Retrieved node types',
    icon: List,
    isWorkflow: true,
  },
  // CRM field tools
  add_field_to_object: {
    label: 'Add Field',
    description: 'Adding field to object',
    doneDescription: 'Field added',
    icon: PlusCircle,
    isCrmField: true,
  },
  edit_field_on_object: {
    label: 'Edit Field',
    description: 'Editing field',
    doneDescription: 'Field updated',
    icon: Pencil,
    isCrmField: true,
  },
};

function formatToolName(name) {
  return name
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function getToolMeta(name) {
  if (TOOL_META[name]) return TOOL_META[name];
  const label = formatToolName(name);
  return {
    label,
    description: `Running ${label}`,
    doneDescription: `Finished ${label}`,
    icon: Cog,
  };
}

function FileOutput({ fileId }) {
  const [isImage, setIsImage] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const token = localStorage.getItem('token');
  const authParam = token ? `?token=${encodeURIComponent(token)}` : '';
  const fileUrl = `${API_BASE_URL}/chat/files/${fileId}`;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const downloadUrl = `${fileUrl}${authParam}`;
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1] : fileId;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-md overflow-hidden border border-gray-200 dark:border-gray-700/80">
      {isImage ? (
        <img
          src={`${fileUrl}${authParam}`}
          alt="Generated output"
          crossOrigin="anonymous"
          className="max-w-full rounded-md"
          onError={() => setIsImage(false)}
        />
      ) : (
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 px-2.5 py-2 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {downloading ? 'Downloading…' : 'Download file'}
        </button>
      )}
    </div>
  );
}

function FileOutputs({ fileIds }) {
  if (!fileIds || fileIds.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {fileIds.map(fileId => (
        <FileOutput key={fileId} fileId={fileId} />
      ))}
    </div>
  );
}

const SLOW_TOOLS = new Set(['bash_code_execution', 'text_editor_code_execution', 'web_fetch']);

// Set of all workflow tool names for quick lookup
const WORKFLOW_TOOL_NAMES = new Set([
  'create_workflow', 'add_workflow_node', 'connect_workflow_nodes',
  'configure_workflow_node', 'activate_workflow', 'get_workflow_summary',
  'remove_workflow_node', 'delete_workflow',
  'list_user_forms', 'list_user_object_definitions',
  'list_user_workflows', 'list_available_node_types',
]);

// Set of CRM field tool names
const CRM_FIELD_TOOL_NAMES = new Set([
  'add_field_to_object', 'edit_field_on_object',
]);

/**
 * Parse the JSON output string from a workflow or CRM field tool call into a summary string.
 * Shows compact, human-readable text instead of raw JSON.
 */
function getToolCallSummary(name, output) {
  if (!output) return null;
  try {
    const data = JSON.parse(output);
    if (!data.success && data.error) {
      return { type: 'error', text: data.error };
    }

    switch (name) {
      case 'create_workflow':
        return { type: 'success', text: data.message || `Created "${data.name}"` };
      case 'add_workflow_node':
        return { type: 'success', text: data.message || `Added ${data.label}`, warning: data.config_warning };
      case 'connect_workflow_nodes':
        return { type: 'success', text: data.message || 'Connected nodes' };
      case 'configure_workflow_node':
        return { type: 'success', text: data.message || 'Configured node' };
      case 'activate_workflow':
        return { type: 'success', text: data.message || 'Workflow activated' };
      case 'get_workflow_summary':
        return { type: 'success', text: `${data.name}: ${data.node_count} nodes, ${data.edge_count} connections (${data.status})` };
      case 'remove_workflow_node':
        return { type: 'success', text: data.message || 'Node removed' };
      case 'delete_workflow':
        return { type: 'success', text: data.message || 'Workflow deleted' };
      case 'list_user_forms':
        return { type: 'info', text: data.message || `${data.count} form(s)` };
      case 'list_user_object_definitions':
        return { type: 'info', text: data.message || `${data.count} object type(s)` };
      case 'list_user_workflows':
        return { type: 'info', text: `${data.count} workflow(s)` };
      case 'list_available_node_types':
        return { type: 'info', text: `${data.triggers?.length || 0} triggers, ${data.actions?.length || 0} actions` };
      // CRM field tools
      case 'add_field_to_object':
        return { type: 'success', text: data.message || `Added "${data.field?.name}" to ${data.object_name}` };
      case 'edit_field_on_object':
        return { type: 'success', text: data.message || `Updated "${data.field?.name}" on ${data.object_name}` };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export default function ToolUseBlock({ toolUse }) {
  const [expanded, setExpanded] = useState(false);
  const [showSlowHint, setShowSlowHint] = useState(false);
  const { name, input, output, done, file_ids } = toolUse;
  const meta = getToolMeta(name);
  const Icon = meta.icon;
  const isWorkflowTool = WORKFLOW_TOOL_NAMES.has(name);
  const isCrmFieldTool = CRM_FIELD_TOOL_NAMES.has(name);
  const isRichTool = isWorkflowTool || isCrmFieldTool;

  useEffect(() => {
    if (done || !SLOW_TOOLS.has(name)) return;
    const timer = setTimeout(() => setShowSlowHint(true), 6000);
    return () => clearTimeout(timer);
  }, [done, name]);

  // Parse tool output for rich display
  const workflowSummary = isRichTool && done ? getToolCallSummary(name, output) : null;

  // Custom description for workflow / CRM field tools showing contextual info
  const displayDescription = (() => {
    if (!isRichTool) return done ? meta.doneDescription : meta.description;
    if (done && workflowSummary) {
      return workflowSummary.text;
    }
    // Show contextual running description
    if (name === 'add_workflow_node' && input?.label) {
      return done ? `Added "${input.label}"` : `Adding "${input.label}"...`;
    }
    if (name === 'create_workflow' && input?.name) {
      return done ? `Created "${input.name}"` : `Creating "${input.name}"...`;
    }
    if (name === 'add_field_to_object' && input?.name) {
      return done ? `Added "${input.name}"` : `Adding "${input.name}" field...`;
    }
    if (name === 'edit_field_on_object' && input?.name) {
      return done ? `Updated "${input.name}"` : `Updating field...`;
    }
    return done ? meta.doneDescription : meta.description;
  })();

  return (
    <div className="mb-2 rounded-lg border border-gray-200 dark:border-gray-700/80 bg-gray-50/80 dark:bg-gray-800/40 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700/40 transition-colors"
      >
        {done ? (
          workflowSummary?.type === 'error' ? (
            <div className="w-4 h-4 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
              <span className="text-red-500 dark:text-red-400 text-[0.5rem] font-bold">!</span>
            </div>
          ) : (
            <div className="w-4 h-4 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center flex-shrink-0">
              <Check className="w-2.5 h-2.5 text-brand-600 dark:text-brand-400" />
            </div>
          )
        ) : (
          <Loader2 className="w-4 h-4 text-gray-400 dark:text-gray-500 animate-spin flex-shrink-0" />
        )}
        <Icon className={`w-3.5 h-3.5 ${isRichTool ? 'text-brand-400 dark:text-brand-500' : 'text-gray-400 dark:text-gray-500'}`} />
        <span className={`font-medium truncate ${
          workflowSummary?.type === 'error'
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-600 dark:text-gray-300'
        }`}>
          {displayDescription}
        </span>
        {workflowSummary?.warning && (
          <span className="text-amber-500 dark:text-amber-400 font-normal text-[0.6875rem] truncate">
            (config incomplete)
          </span>
        )}
        {showSlowHint && !done && (
          <span className="text-gray-400 dark:text-gray-500 font-normal">
            — This may take a minute or two
          </span>
        )}
        <ChevronRight className={`w-3 h-3 ml-auto text-gray-400 dark:text-gray-500 transition-transform duration-150 flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 pt-0.5 text-xs space-y-2 border-t border-gray-200 dark:border-gray-700/80">
          {input && Object.keys(input).length > 0 && (
            <div>
              <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Input</span>
              <pre className="mt-1 bg-gray-100 dark:bg-gray-800/80 rounded-md px-2.5 py-2 text-gray-700 dark:text-gray-300 overflow-x-auto font-mono leading-relaxed">
                {JSON.stringify(input, null, 2)}
              </pre>
            </div>
          )}
          {output && (
            <div>
              <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Output</span>
              <ToolCallResultRenderer toolName={name} output={output} />
            </div>
          )}
        </div>
      )}
      {file_ids && file_ids.length > 0 && (
        <div className="px-3 pb-2.5">
          <FileOutputs fileIds={file_ids} />
        </div>
      )}
    </div>
  );
}
